// Tema warna per jenis payment request, supaya Internal (IPR) dan Operasional
// (OPR) langsung bisa dibedakan di setiap tabel. Kelas Tailwind ditulis
// lengkap (bukan dirakit dari potongan string) agar tidak terbuang saat build.

export const REQUEST_THEMES = {
  internal: {
    key: 'internal',
    label: 'Internal',
    code: 'IPR',
    title: 'Internal Payment Requests',
    subtitle: 'Payment request dari user Internal (Proses Bayar)',
    card: 'border-l-4 border-l-purple-500',
    icon: 'text-purple-600',
    headRow: 'bg-slate-50',
    badge: 'bg-purple-100 text-purple-700 border border-purple-200',
    bulkCollection: 'internal_payment_requests',
    bulkLabel: 'Internal Payment Request',
    banks: [
      { value: 'BSI', label: 'BSI', btn: 'bg-emerald-600 hover:bg-emerald-700', text: 'text-emerald-600', chip: 'bg-emerald-100 text-emerald-700' },
      { value: 'BRI', label: 'BRI', btn: 'bg-blue-600 hover:bg-blue-700', text: 'text-blue-600', chip: 'bg-blue-100 text-blue-700' },
    ],
    empty: 'Belum ada internal payment request',
    testPrefix: 'internal',
  },
  operasional: {
    key: 'operasional',
    label: 'Operasional',
    code: 'OPR',
    title: 'Operasional Payment Requests',
    subtitle: 'Payment request dari user Operasional (Proses Bayar)',
    card: 'border-l-4 border-l-[#800000]',
    icon: 'text-[#800000]',
    headRow: 'bg-[#fbeaea]',
    badge: 'bg-[#f6dede] text-[#800000] border border-[#e8bcbc]',
    bulkCollection: 'operational_payment_requests',
    bulkLabel: 'Operasional Payment Request',
    banks: [
      // Antrean Ops memakai keluarga warna bank yang sama (hijau BSI, biru BRI)
      // dengan nada lebih gelap + penanda "OPS", supaya tetap beda dari BSI/BRI biasa.
      { value: 'BSI_OPS', label: 'BSI Ops', bank: 'BSI', ops: true, btn: 'bg-emerald-800 hover:bg-emerald-900 ring-2 ring-emerald-300', text: 'text-emerald-800', chip: 'bg-emerald-200 text-emerald-900 ring-1 ring-emerald-500' },
      { value: 'BRI_OPS', label: 'BRI Ops', bank: 'BRI', ops: true, btn: 'bg-blue-800 hover:bg-blue-900 ring-2 ring-blue-300', text: 'text-blue-800', chip: 'bg-blue-200 text-blue-900 ring-1 ring-blue-500' },
    ],
    empty: 'Belum ada operasional payment request',
    testPrefix: 'ops',
  },
};

export const requestTheme = (category) =>
  REQUEST_THEMES[category === 'operasional' ? 'operasional' : 'internal'];

// Label & warna antrean bank (Proses Bank, Upload Bukti).
export const BANK_LABELS = { BSI: 'BSI', BRI: 'BRI', BSI_OPS: 'BSI Ops', BRI_OPS: 'BRI Ops' };
export const BANK_CHIP = {
  BSI: 'bg-emerald-100 text-emerald-700',
  BRI: 'bg-blue-100 text-blue-700',
  BSI_OPS: 'bg-emerald-200 text-emerald-900 ring-1 ring-emerald-500',
  BRI_OPS: 'bg-blue-200 text-blue-900 ring-1 ring-blue-500',
};
export const bankLabel = (bt) => BANK_LABELS[bt] || bt || '-';
export const bankChip = (bt) => BANK_CHIP[bt] || 'bg-slate-100 text-slate-600';

// Pesan error API: 422 dari FastAPI berupa array {msg}, bukan string.
export function apiErrorMessage(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail.length) {
    return detail.map((d) => String(d.msg || '').replace(/^Value error, /, '')).filter(Boolean).join('; ') || fallback;
  }
  return fallback;
}

// ── Cakupan approval ─────────────────────────────────────────────
// Sama dengan backend dependencies.approval_scope: hanya untuk role approval;
// kosong = "semua". Server tetap penentu akhir (403) - ini hanya agar UI tidak
// menampilkan tabel/tab yang pasti ditolak.
export const approvalScope = (user) =>
  user?.role === 'approval' && ['umum', 'operasional'].includes(user?.approval_scope) ? user.approval_scope : 'semua';

const SCOPE_KINDS = {
  umum: ['invoice', 'dp', 'internal', 'BSI', 'BRI', 'ipr'],
  operasional: ['operasional', 'BSI_OPS', 'BRI_OPS', 'opr'],
};
export const scopeAllows = (user, kind) => {
  const s = approvalScope(user);
  return s === 'semua' || SCOPE_KINDS[s].includes(kind);
};
export const allowedBanks = (user) => ['BSI', 'BRI', 'BSI_OPS', 'BRI_OPS'].filter((b) => scopeAllows(user, b));
// Kategori master yang dikunci (ipr/opr), atau null bila bebas memilih.
export const lockedMasterCategory = (user) => ({ umum: 'ipr', operasional: 'opr' }[approvalScope(user)] || null);
export const SCOPE_LABELS = { semua: 'Semua', umum: 'Umum', operasional: 'Operasional' };
export const scopeLabel = (user) => {
  if (user?.role !== 'approval') return null;
  const s = approvalScope(user);
  return s === 'semua' ? 'Approval' : `Approval ${SCOPE_LABELS[s]}`;
};

// Kategori master data (Tipe Request, Perusahaan, Data Penerima).
export const MASTER_CATEGORIES = [
  { value: 'ipr', label: 'IPR', badge: 'bg-purple-100 text-purple-700 border border-purple-200' },
  { value: 'opr', label: 'OPR', badge: 'bg-[#f6dede] text-[#800000] border border-[#e8bcbc]' },
];
export const masterCategory = (v) => MASTER_CATEGORIES.find((c) => c.value === v) || MASTER_CATEGORIES[0];

// Purchasing, Gudang, Clerk, AP (dan Internal) hanya boleh mengakses perusahaan
// kategori IPR - sama dengan backend dependencies.COMPANY_IPR_ONLY_ROLES.
export const COMPANY_IPR_ONLY_ROLES = ['purchasing', 'staff_gudang', 'staff_clerk', 'account_payable', 'internal'];
export const lockedCompanyCategory = (user) => (COMPANY_IPR_ONLY_ROLES.includes(user?.role) ? 'ipr' : null);
